/**
 * Quote Feature - RobotixMarket
 * Handles inquiry form modal and quote requests
 * Sends emails via Shopify contact form to info@robotixmarket.com
 */

(function() {
  'use strict';

  const QuoteFeature = {
    selectors: {
      // Triggers
      productTrigger: '[data-quote-inquiry-trigger]',
      cartTrigger: '[data-cart-quote-trigger]',
      
      // Modals (separate for product and cart)
      productOverlay: '[data-quote-overlay="product"]',
      productModal: '[data-quote-modal="product"]',
      cartOverlay: '[data-quote-overlay="cart"]',
      cartModal: '[data-quote-modal="cart"]',
      
      // Common elements within modals
      close: '[data-quote-close]',
      form: '[data-quote-form]',
      submit: '[data-quote-submit]',
      success: '[data-quote-success]',
      error: '[data-quote-error]',
      
      // Cart specific
      cartItems: '[data-cart-items]',
      cartItemsList: '[data-quote-cart-items-list]',
      cartItemsContainer: '[data-quote-items-container]',
      
      // Product info elements
      productInfo: '[data-quote-product-info]',
      productImage: '[data-quote-product-image]',
      productTitle: '[data-quote-product-title]',
      productSku: '[data-quote-product-sku]',
      
      // Hidden fields
      fieldProductId: '[data-quote-field-product-id]',
      fieldProductTitle: '[data-quote-field-product-title]',
      fieldProductHandle: '[data-quote-field-product-handle]',
      fieldProductUrl: '[data-quote-field-product-url]',
      fieldVariantId: '[data-quote-field-variant-id]',
      fieldVariantSku: '[data-quote-field-variant-sku]',
      fieldQuantity: '[data-quote-field-quantity]'
    },

    currentModalType: null,

    init() {
      this.bindEvents();
      this.checkFormSubmissionSuccess();
    },
    
    checkFormSubmissionSuccess() {
      // Check if we're returning from a successful form submission
      const submitted = sessionStorage.getItem('quote_form_submitted');
      if (submitted === 'true') {
        const formType = sessionStorage.getItem('quote_form_type') || 'product';
        
        // Clear the flags
        sessionStorage.removeItem('quote_form_submitted');
        sessionStorage.removeItem('quote_form_type');
        
        // Show success notification
        this.showSuccessNotification(formType);
      }
    },
    
    showSuccessNotification(formType) {
      const message = formType === 'cart' 
        ? 'Thank you! Your quote request has been submitted.'
        : 'Thank you! Your inquiry has been submitted.';
      
      // Create notification element
      const notification = document.createElement('div');
      notification.className = 'quote-success-notification';
      notification.innerHTML = `
        <div class="quote-success-notification__content">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
          <span>${message}</span>
          <button type="button" class="quote-success-notification__close" aria-label="Close">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      `;
      
      document.body.appendChild(notification);
      
      // Trigger animation
      requestAnimationFrame(() => {
        notification.classList.add('active');
      });
      
      // Close button handler
      notification.querySelector('.quote-success-notification__close').addEventListener('click', () => {
        notification.classList.remove('active');
        setTimeout(() => notification.remove(), 300);
      });
      
      // Auto-hide after 5 seconds
      setTimeout(() => {
        if (notification.parentNode) {
          notification.classList.remove('active');
          setTimeout(() => notification.remove(), 300);
        }
      }, 5000);
    },

    bindEvents() {
      // Product inquiry trigger
      document.addEventListener('click', (e) => {
        const productTrigger = e.target.closest(this.selectors.productTrigger);
        if (productTrigger) {
          e.preventDefault();
          e.stopPropagation();
          this.handleProductTriggerClick(e, productTrigger);
          return;
        }

        // Cart quote trigger
        const cartTrigger = e.target.closest(this.selectors.cartTrigger);
        if (cartTrigger) {
          e.preventDefault();
          e.stopPropagation();
          this.openModal('cart');
          return;
        }

        // Close button
        if (e.target.closest(this.selectors.close)) {
          this.closeModal();
          return;
        }

        // Overlay click to close
        if (e.target.matches('[data-quote-overlay]')) {
          this.closeModal();
          return;
        }
      });

      // ESC key to close
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.closeModal();
      });

      // Form submission
      document.addEventListener('submit', (e) => {
        if (e.target.matches(this.selectors.form)) {
          this.handleSubmit(e);
        }
      });
    },

    handleProductTriggerClick(e, trigger) {
      // Try to get product info from the page context
      const productSection = trigger.closest('[data-section]') || trigger.closest('.product') || trigger.closest('quick-view-modal');
      
      let productData = null;
      
      if (productSection) {
        const titleEl = productSection.querySelector('.product__title, .product-card__name, h1, h2');
        const imageEl = productSection.querySelector('.product__media img, .product-card__media img, img');
        const skuEl = productSection.querySelector('.product__sku, [data-sku]');
        const formEl = productSection.querySelector('form[action*="/cart/add"]');
        
        if (titleEl || formEl) {
          productData = {
            title: titleEl ? titleEl.textContent.trim() : '',
            image: imageEl ? imageEl.src : '',
            sku: skuEl ? skuEl.textContent.replace('SKU:', '').trim() : '',
            variantId: formEl ? formEl.querySelector('input[name="id"]')?.value : '',
            handle: window.location.pathname.split('/products/')[1]?.split('?')[0] || '',
            url: window.location.href
          };
        }
      }
      
      this.openModal('product', productData);
    },

    openModal(type = 'product', productData = null) {
      this.currentModalType = type;
      
      const overlay = type === 'cart' 
        ? document.querySelector(this.selectors.cartOverlay)
        : document.querySelector(this.selectors.productOverlay);
      const modal = type === 'cart'
        ? document.querySelector(this.selectors.cartModal)
        : document.querySelector(this.selectors.productModal);
      
      if (!overlay || !modal) {
        console.error('Modal not found for type:', type);
        return;
      }
      
      // Update product info if provided (for quick view)
      if (productData && type === 'product') {
        this.updateProductInfo(modal, productData);
      }
      
      // Reset form state
      this.hideMessages(modal);
      const form = modal.querySelector(this.selectors.form);
      if (form) {
        form.style.display = '';
      }
      
      overlay.classList.add('active');
      modal.classList.add('active');
      
      // Focus first input
      setTimeout(() => {
        const firstInput = modal.querySelector('input:not([type="hidden"])');
        if (firstInput) firstInput.focus();
      }, 150);

      // If cart type, populate cart items
      if (type === 'cart') {
        this.populateCartItems(modal);
      }

      document.body.style.overflow = 'hidden';
    },

    updateProductInfo(modal, data) {
      const productInfo = modal.querySelector(this.selectors.productInfo);
      const imageEl = modal.querySelector(this.selectors.productImage);
      const titleEl = modal.querySelector(this.selectors.productTitle);
      const skuEl = modal.querySelector(this.selectors.productSku);

      if (productInfo) {
        productInfo.style.display = data.title ? '' : 'none';
      }
      if (imageEl && data.image) {
        imageEl.src = data.image;
        imageEl.alt = data.title;
      }
      if (titleEl) {
        titleEl.textContent = data.title;
      }
      if (skuEl) {
        skuEl.textContent = data.sku ? 'SKU: ' + data.sku : '';
        skuEl.style.display = data.sku ? '' : 'none';
      }

      // Update hidden fields
      const fieldTitle = modal.querySelector(this.selectors.fieldProductTitle);
      const fieldHandle = modal.querySelector(this.selectors.fieldProductHandle);
      const fieldUrl = modal.querySelector(this.selectors.fieldProductUrl);
      const fieldVariantId = modal.querySelector(this.selectors.fieldVariantId);
      const fieldVariantSku = modal.querySelector(this.selectors.fieldVariantSku);

      if (fieldTitle) fieldTitle.value = data.title || '';
      if (fieldHandle) fieldHandle.value = data.handle || '';
      if (fieldUrl) fieldUrl.value = data.url || window.location.href;
      if (fieldVariantId) fieldVariantId.value = data.variantId || '';
      if (fieldVariantSku) fieldVariantSku.value = data.sku || '';
    },

    closeModal() {
      // Close all modals
      document.querySelectorAll('[data-quote-overlay]').forEach(el => el.classList.remove('active'));
      document.querySelectorAll('[data-quote-modal]').forEach(el => el.classList.remove('active'));
      
      document.body.style.overflow = '';
      this.currentModalType = null;
    },

    hideMessages(modal) {
      const success = modal.querySelector(this.selectors.success);
      const error = modal.querySelector(this.selectors.error);
      if (success) success.style.display = 'none';
      if (error) error.style.display = 'none';
    },

    showSuccess(modal) {
      const success = modal.querySelector(this.selectors.success);
      const form = modal.querySelector(this.selectors.form);
      if (success) success.style.display = 'block';
      if (form) form.style.display = 'none';
    },

    showError(modal, message) {
      const error = modal.querySelector(this.selectors.error);
      if (error) {
        error.textContent = message || 'Something went wrong. Please try again.';
        error.style.display = 'block';
      }
    },

    async populateCartItems(modal) {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        
        const cartItemsInput = modal.querySelector(this.selectors.cartItems);
        const cartItemsContainer = modal.querySelector(this.selectors.cartItemsContainer);
        const cartItemsList = modal.querySelector(this.selectors.cartItemsList);
        
        if (cart.items && cart.items.length > 0) {
          const items = cart.items.map(item => ({
            id: item.id,
            variant_id: item.variant_id,
            product_id: item.product_id,
            title: item.product_title,
            variant_title: item.variant_title,
            quantity: item.quantity,
            price: item.price,
            sku: item.sku,
            url: item.url
          }));
          
          if (cartItemsInput) {
            cartItemsInput.value = JSON.stringify(items);
          }
          
          if (cartItemsContainer) {
            cartItemsContainer.innerHTML = items.map(item => {
              const name = item.variant_title 
                ? `${item.title} - ${item.variant_title}` 
                : item.title;
              return `<li>
                <span class="quote-inquiry-modal__item-name">${name}</span>
                <span class="quote-inquiry-modal__item-qty">Qty: ${item.quantity}</span>
              </li>`;
            }).join('');
          }
          
          if (cartItemsList) {
            cartItemsList.style.display = '';
          }
        } else {
          if (cartItemsList) {
            cartItemsList.style.display = 'none';
          }
        }
      } catch (e) {
        console.error('Failed to fetch cart:', e);
      }
    },

    async handleSubmit(e) {
      e.preventDefault();
      
      const form = e.target;
      const modal = form.closest('[data-quote-modal]');
      const submitBtn = form.querySelector(this.selectors.submit);
      const submitText = submitBtn.querySelector('.js-quote-submit-text');
      const originalText = submitText ? submitText.textContent : 'Submit';
      
      // Disable submit
      submitBtn.disabled = true;
      if (submitText) submitText.textContent = 'Submitting...';

      try {
        const formData = new FormData(form);
        const data = Object.fromEntries(formData.entries());
        
        // Get quantity from the page if not in form
        if (!data.quantity && data.form_type !== 'cart') {
          const qtyInput = document.querySelector('.product-form__quantity input[type="number"]');
          if (qtyInput) data.quantity = qtyInput.value;
        }
        
        // Parse cart items if present
        if (data.cart_items) {
          try {
            data.cart_items = JSON.parse(data.cart_items);
          } catch (e) {
            data.cart_items = [];
          }
        }

        // Send the quote/inquiry request
        const response = await this.sendContactForm(data);
        
        if (response.success) {
          this.showSuccess(modal);
          form.reset();
        } else {
          this.showError(modal, response.error);
        }
      } catch (err) {
        console.error('Quote submission error:', err);
        this.showError(modal, 'Network error. Please try again.');
      } finally {
        submitBtn.disabled = false;
        if (submitText) submitText.textContent = originalText;
      }
    },

    async sendContactForm(data) {
      return new Promise((resolve) => {
        try {
          // Determine if this is an inquiry or quote request
          const isQuote = data.form_type === 'cart';
          
          // Build the message body with ALL details
          let body = isQuote ? '[ QUOTE REQUEST ]\n\n' : '[ PRODUCT INQUIRY ]\n\n';
          
          // Customer details
          if (data.customer_phone) body += `Phone: ${data.customer_phone}\n`;
          if (data.customer_company) body += `Company: ${data.customer_company}\n`;
          if (data.customer_city) body += `Delivery City: ${data.customer_city}\n`;
          body += '\n';

          // Product/Cart details
          if (isQuote && data.cart_items && data.cart_items.length > 0) {
            body += 'Items Requested:\n';
            data.cart_items.forEach((item, i) => {
              body += `${i + 1}. ${item.title}`;
              if (item.variant_title) body += ` - ${item.variant_title}`;
              body += ` (Qty: ${item.quantity})`;
              if (item.sku) body += ` [SKU: ${item.sku}]`;
              body += '\n';
            });
          } else {
            body += `Product: ${data.product_title || 'Not specified'}\n`;
            body += `Quantity: ${data.quantity || 1}\n`;
            if (data.variant_sku) body += `SKU: ${data.variant_sku}\n`;
            if (data.product_url) body += `URL: ${data.product_url}\n`;
          }

          if (data.message) {
            body += '\nMessage:\n' + data.message;
          }

          // Create a proper Shopify contact form
          const form = document.createElement('form');
          form.method = 'POST';
          form.action = '/contact#QuoteForm';
          form.id = 'QuoteContactForm';
          form.acceptCharset = 'UTF-8';
          form.style.position = 'absolute';
          form.style.left = '-9999px';
          
          // Standard Shopify contact form fields
          const fields = {
            'form_type': 'contact',
            'utf8': '✓',
            'contact[Name]': data.customer_name,
            'contact[email]': data.customer_email,
            'contact[Market]': window.location.hostname,
            'contact[Currency]': (window.Shopify && Shopify.currency && Shopify.currency.active) || '',
            'contact[Message]': body
          };
          
          for (const [name, value] of Object.entries(fields)) {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = value;
            form.appendChild(input);
          }
          
          document.body.appendChild(form);
          
          // Store success state for when page returns
          sessionStorage.setItem('quote_form_submitted', 'true');
          sessionStorage.setItem('quote_form_type', isQuote ? 'cart' : 'product');
          
          // Use Shopify.captcha.protect - this is set up by captcha-bootstrap
          // It queues the form and loads hCaptcha script automatically
          if (window.Shopify && window.Shopify.captcha && window.Shopify.captcha.protect) {
            // protect(form, callback) - callback is called when captcha is ready
            window.Shopify.captcha.protect(form, () => {
              // Captcha is now bound, trigger submit via event
              const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
              form.dispatchEvent(submitEvent);
            });
          } else {
            // Fallback: try direct form submission
            console.warn('Shopify captcha not available, attempting direct submit');
            form.submit();
          }
          
        } catch (error) {
          console.error('Contact form error:', error);
          resolve({ success: false, error: error.message });
        }
      });
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => QuoteFeature.init());
  } else {
    QuoteFeature.init();
  }
})();
